/**
 * The admin app's ABDM Bridge page: DRCFO's software bridge as the ABDM gateway
 * holds it (§3.2.7 Find services by bridge id) and the change of its callback
 * URL (§3.2.4 Update bridge URL), both through drcfo.backend
 * (services/abdmBridge.ts) to abdm.backend.
 *
 * GET /admin/abdm/bridge answers `{ bridge, services, gateway, canUpdateUrl,
 * checkedAt }`: `bridge` is `{ id, name, url, active, blocklisted, entity }`,
 * each service `{ id, name, types, active }`, `gateway` "sandbox" or
 * "production", and `canUpdateUrl` false in production, where ABDM's
 * Integration Support Team changes the URL (ABDM FAQ #3).
 *
 * The callback URL is a base: the gateway adds `/api/v3/<path>` to it (FAQ
 * #30), so a base ending in `/api` or `/v3` only works behind a proxy that
 * strips the extra segment; the dialog warns about one. The checks here refuse
 * what the backends answer 400 to, so the dialog can say what is wrong before
 * anything is sent: https in lower case, at most 512 characters, no
 * credentials, query or fragment, and only the characters a URL may carry.
 * Both backends check the URL with Joi's uri() (RFC 3986), which is stricter
 * than the browser's URL parser: that one quietly encodes `|` or a non-ASCII
 * letter, turns `\` into `/` and reads `HTTPS://` or `https:///host` as an
 * ordinary https URL, so it cannot be the check on its own.
 */

export const BRIDGE_URL_MAX = 512;

/** One callback the gateway makes, to show what the base turns into. */
export const CALLBACK_PATH = "/api/v3/hip/patient/care-context/discover";

// A base ending in one of these makes the gateway call `<base>/api/v3/...`
// with the segment twice.
const DOUBLED = ["/api/v3", "/api", "/v3"];

const trimmed = (value) => String(value ?? "").trim();

// RFC 3986 allows no character outside printable ASCII, nor any of these, in a
// URL as written. `[` and `]` are allowed around an IPv6 host only, so they are
// checked on the path once the URL is parsed.
const NOT_IN_A_URL = /[^\x21-\x7e]|[\\"<>^`{|}]/u;

// The character by itself, and its code point when it may not show (a
// zero-width space pasted with the URL, say).
const cannotContain = (char) => {
  if (char === '"') return `The URL cannot contain '"'.`;
  if (/^[\x21-\x7e]$/.test(char)) return `The URL cannot contain "${char}".`;
  const code = char.codePointAt(0).toString(16).toUpperCase().padStart(4, "0");
  return `The URL cannot contain "${char}" (U+${code}).`;
};

/** `base` as the gateway calls it for patient discovery; "" with no base. */
export function callbackExample(base) {
  const text = trimmed(base).replace(/\/+$/, "");
  return text ? `${text}${CALLBACK_PATH}` : "";
}

/**
 * The new callback URL as typed, checked: `{ url, error, warning }`. `url` is
 * the trimmed value to send; `error` (a reason it cannot be sent) or `warning`
 * (it can, but is probably wrong) is "" when there is none.
 */
export function checkBridgeUrl(value, current) {
  const url = trimmed(value);
  const result = (error, ...warnings) => ({
    url,
    error,
    warning: warnings.filter(Boolean).join(" "),
  });

  if (!url) return result("Enter the new callback URL.");
  if (url.length > BRIDGE_URL_MAX) {
    return result(`The URL is longer than ${BRIDGE_URL_MAX} characters.`);
  }
  if (/\s/.test(url)) return result("The URL must not contain spaces.");
  const stray = url.match(NOT_IN_A_URL);
  if (stray) return result(cannotContain(stray[0]));

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return result("Enter a full URL, starting with https://.");
  }
  if (parsed.protocol !== "https:") {
    return result("The URL must start with https://; ABDM calls https only.");
  }
  // The parser reads `HTTPS://host`, `https:/host` and `https:///host` all as
  // https://host/; the backends refuse each of them.
  if (!/^https:\/\/[^/]/.test(url)) {
    return result(
      url.startsWith("https:")
        ? "Enter a full URL, starting with https://."
        : "Write https:// in lower case."
    );
  }
  if (parsed.username || parsed.password) {
    return result("The URL must not carry a user name or password.");
  }
  if (/[?#]/.test(url)) {
    return result("The URL must not have a query (?) or a fragment (#).");
  }
  const bracket = parsed.pathname.match(/[[\]]/);
  if (bracket) return result(cannotContain(bracket[0]));

  // Not a refusal: the page shows the URL as of its last read, and another
  // developer on the bridge may have changed it since.
  const same =
    url === trimmed(current)
      ? "The last read shows this URL already. If someone may have changed it since, cancel and refresh the page first."
      : "";

  const path = parsed.pathname.replace(/\/+$/, "").toLowerCase();
  const suffix = DOUBLED.find((end) => path.endsWith(end));
  const doubled = suffix
    ? `ABDM adds /api/v3/<path> to this URL, so it would call ${callbackExample(url)}. That fails silently unless a proxy there strips the extra "${suffix}"; the usual value is ${parsed.origin}/.`
    : "";
  return result("", same, doubled);
}

const TYPE_LABELS = {
  HIP: "HIP",
  HIU: "HIU",
  PHR: "PHR",
  HEALTH_LOCKER: "Health locker",
};

/** A service type as the page shows it. */
export function serviceTypeLabel(type) {
  const text = trimmed(type);
  return TYPE_LABELS[text] || text.replace(/_/g, " ");
}

/**
 * What is wrong with the bridge itself, as `[{ key, severity, title,
 * message }]`; empty when ABDM marks it active and not blocklisted, or says
 * nothing either way.
 */
export function bridgeAlerts(bridge) {
  const alerts = [];
  if (bridge?.blocklisted === true) {
    alerts.push({
      key: "blocklisted",
      severity: "error",
      title: "ABDM has blocklisted this bridge",
      message:
        "Linking and record sharing for every facility on it can fail. Ask the ABDM Integration Support Team why.",
    });
  }
  if (bridge?.active === false) {
    alerts.push({
      key: "inactive",
      severity: "error",
      title: "ABDM marks this bridge inactive",
      message:
        "Linking and record sharing for every facility on it can fail. Ask the ABDM Integration Support Team to reactivate it.",
    });
  }
  return alerts;
}

/** What the page says after a change the backend accepted. */
export function bridgeChangeOutcome({ url, previousUrl } = {}) {
  const now = trimmed(url);
  const before = trimmed(previousUrl);
  const changed = before
    ? `Callback URL changed from ${before} to ${now}.`
    : `Callback URL changed to ${now}.`;
  return `${changed} ABDM may keep calling the old URL for a few minutes.`;
}
